<template>
  <div>
    <div class="signup-container">
      <div class="app-header">
        <img :alt="$config.client.name" :src="logoPath"/>
      </div>
      <b-overlay
        id="overlay-background"
        :show="loading"
        :variant="'white'"
        :opacity="0.7"
        :blur="'2px'"
        rounded="sm"
        no-wrap
      />
      <div class="signup-form">
        <h4 class="title">
          {{ $t('signup.title') }}
          <span class="sub-title">{{ $t('signup.subtitle') }}</span>
        </h4>
        <hr class="divider">
        <h5>{{ $t('signup.secondTitle') }}</h5>

        <ValidationObserver ref="form" v-slot="{ handleSubmit }">
          <form>
            <div class="required-help">* Champs obligatoires</div>

            <h5>{{ $t('signup.form.personalDetails.title') }}</h5>
            <hr class="divider">

            <!-- FIRST NAME -->
            <div
              v-if="isFieldVisible('first_name', formConfig.hiddenFields)"
              class="row g-2 align-items-center"
            >
              <div class="col-3">
                <label
                  :class="isFieldRequired('first_name', formConfig.requiredFields)"
                  class="col-form-label left-aligned"
                  for="signUp-firstname"
                >
                  {{ $t('words.firstname') }}
                </label>
              </div>
              <div class="col">
                <ValidationProvider
                  ref="first_name"
                  :rules="isFieldRequired('first_name', formConfig.requiredFields)"
                  v-slot="{ classes, errors }"
                >
                  <div class="control" :class="classes">
                    <input
                      v-model="form.first_name"
                      id="signUp-firstname"
                      data-test="signUp-firstname"
                      type="text"
                      class="form-control"
                      :placeholder="$t('words.firstname')"
                    >
                    <span class="form-errors">{{ errors[0] }}</span>
                  </div>
                </ValidationProvider>
              </div>
            </div>

            <!-- LAST NAME -->
            <div  data-test="signUp-lastname"
              v-if="isFieldVisible('last_name', formConfig.hiddenFields)"
              class="row g-2 align-items-center"
            >
              <div class="col-3">
                <label
                  :class="isFieldRequired('last_name', formConfig.requiredFields)"
                  class="col-form-label left-aligned"
                  for="signUp-lastname"
                >
                  {{ $t('words.lastname') }}
                </label>
              </div>
              <div class="col">
                <ValidationProvider
                  ref="last_name"
                  :rules="isFieldRequired('last_name', formConfig.requiredFields)"
                  v-slot="{ classes, errors }"
                >
                  <div class="control" :class="classes">
                    <input
                      v-model="form.last_name"
                      id="signUp-lastname"
                      type="text"
                      class="form-control"
                       :placeholder="$t('words.lastname')"
                    >
                    <span class="form-errors">{{ errors[0] }}</span>
                  </div>
                </ValidationProvider>
              </div>
            </div>

            <!-- EMAIL -->
            <div
              v-if="isFieldVisible('email', formConfig.hiddenFields)"
              class="row g-2 align-items-center"
            >
              <div class="col-3">
                <label
                  :class="isFieldRequired('email', formConfig.requiredFields)"
                  class="col-form-label left-aligned"
                  for="signUp-email"
                >
                  {{ $t('words.email') }}
                </label>
              </div>
              <div class="col">
                <ValidationProvider
                  ref="email"
                  :rules="`${isFieldRequired('email', formConfig.requiredFields)}|email`"
                  v-slot="{ classes, errors }"
                >
                  <div class="control" :class="classes">
                    <input
                      v-model="form.email"
                      id="signUp-email"
                      data-test="signUp-email"
                      type="mail"
                      class="form-control"
                      :placeholder="$t('words.email')"
                    >
                    <span class="form-errors">{{ errors[0] }}</span>
                  </div>
                </ValidationProvider>
              </div>
            </div>

            <!-- PHONE NUMBER -->
            <div
              v-if="isFieldVisible('phone_number', formConfig.hiddenFields)"
              class="row g-2 align-items-center"
            >
              <div class="col-3">
                <label
                  :class="isFieldRequired('phone_number', formConfig.requiredFields)"
                  class="col-form-label left-aligned"
                  for="signUp-phone"
                >
                  {{ $t('words.phone') }}
                </label>
              </div>
              <div class="col">
                <ValidationProvider
                  ref="phone_number"
                  :rules="isFieldRequired('phone_number', formConfig.requiredFields)"
                  v-slot="{ classes, errors }"
                >
                  <div class="control" :class="classes">
                    <input
                      v-model="form.phone_number"
                      id="signUp-phone"
                      data-test="signUp-phone"
                      type="text"
                      class="form-control"
                      placeholder=""
                    >
                    <span class="form-errors">{{ errors[0] }}</span>
                  </div>
                </ValidationProvider>
              </div>
            </div>

            <!-- COMMENTS -->
            <div
              v-if="isFieldVisible('comments', formConfig.hiddenFields)"
              class="row g-2 align-items-center"
            >
              <div class="col-3">
                <label
                  class="col-form-label left-aligned"
                  for="signUp-comments"
                >
                  {{ $t('signup.form.personalDetails.reason') }}
                </label>
              </div>
              <div class="col">
                <textarea
                  v-model="form.comments"
                  id="signUp-comments"
                  data-test="signUp-comments"
                  class="form-control"
                />
              </div>
            </div>

            <h5>{{ $t('signup.form.loginDetails.title') }}</h5>
            <hr class="divider">

            <!-- USERNAME -->
            <div class="row g-1 align-items-center">
              <div class="col-3">
                <label
                  class="col-form-label left-aligned"
                  for="signUp-username"
                >
                  {{ $t('words.username') }}
                </label>
              </div>
              <div class="col">
                <ValidationProvider ref="username" rules="required" v-slot="{ classes, errors }">
                  <div class="control" :class="classes">
                    <div class="input-group flex-nowrap">
                      <input
                        v-model="form.username"
                        id="signUp-username"
                        data-test="signUp-username"
                        type="text"
                        class="form-control"
                        disabled
                      >
                      <span class="input-group-text">
                        <b-icon icon="person-fill" />
                      </span>
                    </div>
                    <span class="form-errors">{{ errors[0] }}</span>
                  </div>
                </ValidationProvider>
              </div>
            </div>
            <div class="row g-2 align-items-center" style="margin-bottom">
              <div class="col-3">
              </div>
              <div class="col">
                <p class="infos">
                  {{ $t('signup.form.loginDetails.usernameHelp') }}
                </p>
              </div>
            </div>

            <!-- PASSWORD -->
            <div class="row g-3 align-items-center">
              <div class="col-3">
                <label
                  class="col-form-label left-aligned required"
                  style="padding: 0;"
                  for="signUp-password1"
                >
                  {{ $t('words.password') }}
                </label>
              </div>
              <div class="col">
                <ValidationProvider
                  ref="password"
                  :rules="{
                    required: true,
                    regex: /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d$&+,:;=?@#|'<>.^*()%!-]{8,}$/
                  }"
                  v-slot="{ classes, errors }"
                  vid="confirmation"
                >
                  <div class="control" :class="classes">
                    <div class="input-group flex-nowrap">
                      <input
                        v-model="form.password1"
                        id="signUp-password1"
                        data-test="signUp-password1"
                        class="form-control"
                        :type="showPassword ? 'text' : 'password'"
                        :placeholder="$t('words.password')"
                      >
                      <span class="input-group-text">
                        <b-icon
                          data-test="signUp-toggleShowPassword"
                          :icon="showPassword?'eye-slash-fill':'eye-fill'"
                          @click="showPassword = !showPassword"
                        />
                      </span>
                    </div>
                    <span class="form-errors">{{ errors[0] }}</span>
                  </div>
                </ValidationProvider>
              </div>
            </div>
            <div class="row g-1 align-items-center">
              <div class="col-3"></div>
              <div class="col">
                <ValidationProvider
                  ref="password"
                  rules="required|confirmed:confirmation"
                  v-slot="{ classes, errors }"
                >
                  <div class="control" :class="classes">
                    <div class="input-group flex-nowrap">
                      <input
                        v-model="form.password2"
                        data-test="signUp-password2"
                        class="form-control"
                        :type="showPassword ? 'text' : 'password'"
                        :placeholder="$t('words.password')"
                      >
                      <span class="input-group-text">
                        <b-icon
                          data-test="signUp-toggleShowPassword2"
                          :icon="showPassword?'eye-slash-fill':'eye-fill'"
                          @click="showPassword = !showPassword"
                        />
                      </span>
                    </div>
                    <span class="form-errors">{{ errors[0] }}</span>
                  </div>
                </ValidationProvider>
              </div>
            </div>
            <div class="row g-2 align-items-center">
              <div class="col-3">
              </div>
              <div class="col">
                <div class="infos">
                  <ul>
                    <li>{{ $t('signup.form.loginDetails.passwordHelp')[0] }}</li>
                    <li>{{ $t('signup.form.loginDetails.passwordHelp')[1] }}</li>
                    <li>{{ $t('signup.form.loginDetails.passwordHelp')[2] }}</li>
                    <li>{{ $t('signup.form.loginDetails.passwordHelp')[3] }}</li>
                  </ul>
                </div>
              </div>
            </div>
            <OrganisationSelector
              ref="organisationSelector"
              :subtitle="$tc('signup.form.usergroup.subtitle')"
              :showCreationForm="showCreationForm"
              :showSimpleCreationForm="true"
              :api-error="error"
              @update:showCreationForm="handleShowCreationChange"
              @select="handleOrganisationSelection"
              @create="handleOrganisationCreation"
            />
            <b-button
              :disabled="(
                !form.username ||
                !form.password1 ||
                !form.password2 ||
                !form.first_name ||
                !form.last_name ||
                !form.email
              )"
              :pressed="btnPressed"
              data-test="signUp-submit"
              @click.prevent="handleSubmit(submit)"
              variant="primary"
            >
              {{ $t('words.validate') }}
            </b-button>
            <b-button
              type="button"
              variant="outline-primary"
              data-test="signUp-signIn"
              @click.prevent="$router.push({ name: 'SignIn' })"
            >
              {{ $t('buttons.cancel') }}
            </b-button>
          </form>
        </ValidationObserver>
      </div>
    </div>
    <small class="footer">
      <p>
        {{ $t('footer') }} <a href="https://www.neogeo.fr/" target="_blank" rel="noopener">Neogeo-Technologies</a>
      </p>
    </small>
  </div>
</template>

<script>

import OrganisationSelector from '@/components/OrganisationSelector';

// import usersAPI from '@/api/usersAPI.js';

// import Swal from "sweetalert2";
// import "sweetalert2/dist/sweetalert2.min.css";
import loginAPI from '@/api/loginAPI.js';
import { deburr } from 'lodash';
import { slugify } from '@/utils.js';

import i18n from '@/i18n';
import { ErrorService } from '@/services/error-service.js';

import {
  ValidationObserver,
  ValidationProvider,
  extend,
  configure,
} from 'vee-validate';

import { required, email, confirmed, regex } from 'vee-validate/dist/rules';

extend('required', {
  ...required,
  message: () => i18n.t('errors.required'),
});

extend('email', {
  ...email,
  message: () => i18n.t('errors.email'),
});

extend('confirmed', {
  ...confirmed,
  message: () => i18n.t('errors.confirmPassword'),
});

extend('regex', {
  ...regex,
  message: () => i18n.t('errors.regexPassword'),
});

configure({
  classes: {
    valid: 'is-valid',
    invalid: 'is-invalid',
  },
});

export default {
  name: 'SignUp',
  components: {
    ValidationObserver,
    ValidationProvider,
    OrganisationSelector,
  },
  data() {
    return {
      loading: false,
      showCreationForm: false,
      form: {
        first_name: null,
        last_name: null,
        email: null,
        phone_number: null,
        comments: null,
        username: null,
        password1: null,
        password2: null
      },
      organisationSelected: null,
      organisationToCreate: null,
      organisationThumbnail: null,
      organisationSpheres: [],
      btnPressed: false,
      showPassword: false,
      localError: null,
    };
  },
  computed: {
    error() { return this.localError; },

    formConfig() {
      return this.$config.forms.signup;
    },

    logoPath() {
      return require(process.env.VUE_APP_LOGO);
    }
  },
  watch: {
    '$i18n.locale': function(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.$refs.form.validate();
      }
    },

    'form.first_name': {
      deep: true,
      handler(newValue) {
        if (newValue !== null && this.form.last_name !== null) {
          this.form.username =
            deburr(
              newValue
                .replace(/\s/g, '')
                .charAt(0)
                .toLowerCase()
                .concat('', this.form.last_name.replace(/\s/g, '').toLowerCase())
            ).normalize('NFD').replace(/[\u0300-\u036f]|[^A-Z0-9]/ig, '');
        }
      }
    },
    'form.last_name': {
      deep: true,
      handler(newValue) {
        if (this.form.first_name !== null && newValue !== null) {
          this.form.username =
          deburr(
            this.form.first_name
              .replace(/\s/g, '')
              .charAt(0)
              .toLowerCase()
              .concat('', newValue.replace(/\s/g, '').toLowerCase())
          ).normalize('NFD').replace(/[\u0300-\u036f]|[^A-Z0-9]/ig, '');
        }
      }
    },
    signed() {
      this.$router.push({ name: 'SignUpSuccess' });
    },
    error(newValue) {
      if (newValue) {
        for (const [key, value] of Object.entries(newValue)) {
          if (this.$refs[key]) {
            this.$refs[key].applyResult({
              errors: value,
              valid: false,
              failedRules: {},
            });
          }
        }
      }
    },
  },
  created() {
    if (loginAPI.logged) {
      loginAPI.globalSignOut();
    }
  },
  methods: {
    handleOrganisationSelection(e) {
      this.organisationSelected = e;
    },
    handleOrganisationCreation(e) {
      this.organisationToCreate = e.organisation;
      this.organisationThumbnail = e.thumbnail;
      this.organisationSpheres = e.spheres;
    },
    handleShowCreationChange(newValue) {
			this.showCreationForm = newValue;
			this.organisationSelected = null;
			this.organisationToCreate = null;
			if (this.showCreationForm) {
				this.$refs.organisationSelector.clearOrganisation(); // Clear Selector
			}
    },
    submit() {
      this.loading = true;
      this.btnPressed = true;
      this.signUp();
    },
    async signUp() {
      const payload = {
        first_name: this.form.first_name,
        last_name: this.form.last_name,
        email: this.form.email,
        username: this.form.username,
        password: this.form.password1,
        phone_number: this.form.phone_number ? this.form.phone_number : '',
        comments: this.form.comments ? this.form.comments : '',
        ...(this.organisationSelected && this.organisationSelected.id) && { usergroup_id: this.organisationSelected.id }
      };

      if (this.organisationToCreate) {
        const form = this.organisationToCreate;
        payload.usergroup_to_create = {
          organisation: {
            additional_id: form.additional_id,
            display_name: form.display_name,
            codename: slugify(form.display_name),
            acronym: form.acronym,
            registration_number: form.registration_number,
            organisation_type: form.organisation_type,
            is_active: true,
            postal_address: form.postal_address,
            description: form.description,
            website_url: form.website_url,
            phone_number: form.phone_number,
          },
          display_name: form.display_name,
          codename: slugify(form.display_name),
          usergroup_type: 'organisation'
        };
      }

      try {
        const resp = await loginAPI.signUp(payload);
        if (resp) {
          this.$router.push({ name: 'SignUpSuccess' });
        } else {
          this.loading = false;
        }
      } catch (error) {
        const err = error.response || i18n.t('messages.error');
        ErrorService.onError(err);
        this.localError = err && err.data ? err.data : err;
        if (this.localError) {
          for (const [key, value] of Object.entries(this.localError)) {
            if (this.$refs[key]) {
              this.$refs[key].applyResult({
                errors: value,
                valid: false,
                failedRules: {},
              });
            }
          }
        }
        this.loading = false;
      }
    }
  }
};
</script>

<style lang="less" scoped>
.signup-container {
  position: relative;
  margin: 1rem auto auto;
  width: 800px;
  height: fit-content;

  .signup-form {
    margin: 5rem 1rem;

    h4.title {
      color: #373b3d;

      .sub-title {
        font-size: 75%;
        color: #6b7479;
      }
    }

    hr.solid {
      border-top: 2px solid #373b3d;
    }

    h5 {
      color: #6b7479;
    }

    form {
      margin-top: 32px;

      h5 {
        margin-bottom: 20px;
        margin-top: 40px;
        color: #373b3d;
      }

      .row.g-3 {
        margin-bottom: 2rem;
      }
      .row.g-2 {
        margin-bottom: 1.75rem;
      }
      .row.g-1 {
        margin-bottom: 1.25rem;
      }

      .input-group {
        span {
          cursor: pointer;
          border-bottom-left-radius: 0;
          border-top-left-radius: 0;
          border-left: none
        }
      }

      button {
        float: right;
        position: relative;
        margin-left: 7px;
        margin-top: 30px;
      }

      .infos {
        font-size: 0.8em;
        font-style: italic;
        margin-right: 1em;
        ul {
          padding-left: 1rem;
        }
      }

    }
  }
}

.form-errors {
  color: #EB0600 !important;
  margin-right: 2em;
  line-height: 1;
}

.form-success {
  color: #30C963 !important;
}

.footer {
  position: relative;
  bottom: 0;
  font-size: small;
  margin-top: 2rem;
}

.footer a {
  text-decoration: none;
}

</style>
